import encryption from '@/services/encryption';
import returnAppConfig from '@/helpers/return-app-config';



async function normalizePaymentRequestBody({ contactInfo, paymentGateway, paymentMethod, tdsData, cloverBrowserInfo }, base = {}) {
  try {
    const body = { ...base };
    const assignToBody = (args) => {
      Object.assign(body, args);
    };

    const { cardDetails, billingAddress } = paymentMethod;

    const fullName = contactInfo.fullName
      || cardDetails?.nameOnCard.fullName
      || paymentMethod.body?.nameOnCard;

    if (contactInfo.email) {
      assignToBody({ email: contactInfo.email });
    }

    if (contactInfo.phoneNumber) {
      assignToBody({ phoneNumber: contactInfo.phoneNumber });
    }

    if (fullName) {
      const nameSegments = fullName.trim().split(' ');
      assignToBody({
        firstName: nameSegments.shift(),
        guestName: fullName,
        lastName: nameSegments.join(' ')
      });
    }

    if (paymentMethod.extendedCardData) {
      assignToBody({ extendedCardData: paymentMethod.extendedCardData });
    }

    if (tdsData) {
      assignToBody({ threeDs: tdsData });
    }

    if (cloverBrowserInfo) {
      assignToBody({
        supportsPaymentActions: true,
        threeds: { browserInfo: cloverBrowserInfo }
      });
    }

    if (paymentMethod.giftCards && paymentMethod.giftCards.length > 0) {
      const giftCards = paymentMethod.giftCards.map((card) => {
        if (card.token) {
          return { token: card.token };
        }

        /** Saved cards for registered users will have a cardId, use cardId for saved cards at submit */
        if (card.cardId) {
          return {
            cardId: card.cardId
          };
        }

        /** Can apply gift card at checkout which still requires cardNumber and pin at sumbit */
        return {
          cardNumber: card.cardNumber,
          pin: card.pin
        };
      });

      assignToBody({ giftCards });
    }

    const setCardData = async () => {
      const { unencrypted, encrypted } = await encryption.encryptCardDetails({
        cardDetails,
        paymentGateway
      });

      assignToBody({
        billingAddress,
        cardNumberLastFour: cardDetails.cardNumberLastFour,
        cardType: cardDetails.cardType,
        cardNumber: unencrypted.cardNumber,
        cvv: unencrypted.cvv,
        encryptedCardNumber: encrypted.cardNumber,
        encryptedCvv: encrypted.cvv,
        expiration: {
          month: cardDetails.cardExpiration.month,
          year: cardDetails.cardExpiration.year
        },
        nameOnCard: cardDetails.nameOnCard.fullName,
        paymentType: 'CreditCard',
        token: encrypted.token
      });
    };

    const { event } = paymentMethod;

    switch (paymentMethod.type) {
      case 'none':
        assignToBody({ paymentType: 'InStore' });
        break;

      case 'apple-pay':
        assignToBody({
          billingAddress: { postalCode: event.payment.billingContact.postalCode },
          cardNumberLastFour: event.payment.token.paymentMethod.displayName.replace(/[^0-9]/g, ''),
          cardType: event.payment.token.paymentMethod.network,
          paymentType: 'ApplePay',
          walletToken: paymentMethod.walletTokenOverride || JSON.stringify(paymentMethod.event.payment)
        });
        break;

      case 'card':
        await setCardData();
        break;

      case 'gift-card':
        assignToBody({ paymentType: 'GiftCard' });
        break;

      case 'google-pay':
        assignToBody({
          billingAddress: { postalCode: event.paymentMethodData.info.billingAddress.postalCode },
          cardNumberLastFour: event.paymentMethodData.info.cardDetails,
          cardType: event.paymentMethodData.info.cardNetwork,
          paymentType: 'GooglePay',
          walletToken: paymentMethod.walletTokenOverride || JSON.stringify(paymentMethod.event)
        });
        break;

      case 'offer':
        assignToBody({ paymentType: 'Offer' });
        break;

      case 'cash':
        assignToBody({ paymentType: 'Cash' });
        break;

      case 'room-charge':
        assignToBody({
          paymentType: 'RoomCharge',
          lastName: paymentMethod.roomLastName,
          roomNumber: paymentMethod.roomNumber
        });
        break;

      case 'house-account':
        assignToBody({
          paymentType: 'HouseAccount',
          houseAccountPublicId: paymentMethod.houseAccountPublicId
        });
        break;

      case 'saved-card':
        assignToBody({
          creditCardId: paymentMethod.savedCardId,
          cvv: paymentMethod.cvv,
          paymentType: 'SavedCreditCard'
        });
        break;

      case 'square-alt-payment':
      case 'third-party-form':
        assignToBody(paymentMethod.body);
        break;

      default:
        break;
    }

    if (paymentMethod.testEnvironmentOverride && returnAppConfig().isTestEnvironment) {
      assignToBody(paymentMethod.testEnvironmentOverride);
    }

    if (window._debugPay) {
      window._paymentEvent = paymentMethod.event;
    }

    return body;
  }

  catch (error) {
    throw error;
  }
}



export default normalizePaymentRequestBody;
